// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../vite-env.d.ts" />
import { getMessaging, getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import { initFirebase } from '../lib/firebase';
import { reportError } from '../lib/error-reporting';

/**
 * Detect if running on iOS (iPhone, iPad, iPod)
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor || '';

  // Check for iOS devices
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true;
  }

  // Check for iPad on iOS 13+ (reports as Mac)
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

/**
 * Detect if running on Android
 */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/**
 * Check if running as installed PWA (standalone mode)
 */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;

  // navigator.standalone is iOS Safari specific (non-standard)
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true;
}

/**
 * Result type for notification permission request
 */
export type NotificationResult = {
  success: true;
  token: string;
} | {
  success: false;
  reason: 'not-supported' | 'denied' | 'token-failed' | 'ios-purged' | 'android-overlay' | 'error';
  isIOS: boolean;
  isAndroid: boolean;
};

/**
 * Request notification permission and get FCM token
 * Returns detailed result with error reason for better UX
 */
export async function requestNotificationPermissionWithDetails(): Promise<NotificationResult> {
  const isiOSDevice = isIOS();
  const isAndroidDevice = isAndroid();

  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return { success: false, reason: 'not-supported', isIOS: isiOSDevice, isAndroid: isAndroidDevice };
    }

    // On Android, check if permission was already denied before requesting
    const previousPermission = Notification.permission;

    // Request permission and measure response time
    const startTime = Date.now();
    const permission = await Notification.requestPermission();
    const elapsedTime = Date.now() - startTime;

    if (permission !== 'granted') {
      // On Android, if permission was 'default' and is now 'denied' almost instantly (<500ms),
      // it likely means a screen overlay blocked the permission dialog
      if (isAndroidDevice && previousPermission === 'default' && permission === 'denied' && elapsedTime < 500) {
        console.warn('Android: Permission denied too quickly - likely screen overlay detected');
        return { success: false, reason: 'android-overlay', isIOS: isiOSDevice, isAndroid: true };
      }
      return { success: false, reason: 'denied', isIOS: isiOSDevice, isAndroid: isAndroidDevice };
    }

    // Get FCM token
    const { app } = initFirebase();
    const messaging = getMessaging(app);

    try {
      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      });

      if (token) {
        return { success: true, token };
      }

      // Permission granted but no token - likely iOS purged state
      if (isiOSDevice) {
        return { success: false, reason: 'ios-purged', isIOS: true, isAndroid: false };
      }
      return { success: false, reason: 'token-failed', isIOS: false, isAndroid: isAndroidDevice };
    } catch {
      // Permission was granted but token generation failed
      // On iOS, this likely means the push subscription was purged
      if (isiOSDevice) {
        console.warn('iOS: Permission granted but token failed - likely purged by system');
        return { success: false, reason: 'ios-purged', isIOS: true, isAndroid: false };
      }
      return { success: false, reason: 'token-failed', isIOS: false, isAndroid: isAndroidDevice };
    }
  } catch (error) {
    console.error('Notification permission error:', error);

    // On Android, NotAllowedError can indicate screen overlay issue
    if (isAndroidDevice && error instanceof Error && error.name === 'NotAllowedError') {
      return { success: false, reason: 'android-overlay', isIOS: false, isAndroid: true };
    }
    return { success: false, reason: 'error', isIOS: isiOSDevice, isAndroid: isAndroidDevice };
  }
}

/**
 * Request notification permission and get FCM token
 * Returns the FCM token if permission is granted, null otherwise
 * @deprecated Use requestNotificationPermissionWithDetails for better error handling
 */
export async function requestNotificationPermission(): Promise<string | null> {
  const result = await requestNotificationPermissionWithDetails();
  return result.success ? result.token : null;
}

/**
 * Check if notification permission has been granted
 */
export function hasNotificationPermission(): boolean {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
}

/**
 * Check if notification permission is denied
 */
export function isNotificationPermissionDenied(): boolean {
  if (!('Notification' in window)) {
    return true;
  }
  return Notification.permission === 'denied';
}

/**
 * Setup foreground message listener
 * This handles notifications when the app is in the foreground
 */
export function setupForegroundMessageListener(
  onMessageReceived: (payload: MessagePayload) => void
): () => void {
  try {
    const { app } = initFirebase();
    const messaging = getMessaging(app);

    return onMessage(messaging, (payload) => {
      onMessageReceived(payload);

      // Show a notification even when app is in foreground
      if (payload.notification) {
        // Messages with notification payload (e.g., customer notifications)
        new Notification(payload.notification.title || 'Notification', {
          body: payload.notification.body,
          icon: payload.notification.icon || '/icon-192.png',
          tag: payload.data?.orderId ? `order-${payload.data.orderId}` : undefined,
        });
      } else if (payload.data?.type === 'new_order') {
        // Data-only messages (merchant order notifications)
        new Notification(payload.data.title || 'Nouvelle commande !', {
          body: payload.data.body || 'Vous avez reçu une nouvelle commande.',
          icon: '/pwa-192x192.png',
          tag: payload.data.orderId ? `order-${payload.data.orderId}` : undefined,
        });
      }
    });
  } catch (error) {
    reportError(error, { context: 'setupForegroundMessageListener' });
    return () => {};
  }
}
